import createError from 'axios/lib/core/createError'
import {
  MAX_COOKIE_HEADER_BYTES,
  cookieHeaderSize,
  pruneOversizedCookies,
} from '@/utils/cookieGuard'
import { translateContentLinks } from '@/utils/paths'
import pathTranslations from '@/utils/sitePathTranslations'
import { withContextHeaders } from '@/utils/requestContextHeaders'
import { priceCountry } from '@/utils/priceCountry'

const propertiesToTranslate = [
  'description_bottom',
  'description_top',
  'body',
  'variant',
  'questions',
  'value',
]

const summarizeUrl = url =>
  String(url ?? '')
    .split('?')[0]
    .replace(/(\/review_token\/)[^/]+/, '$1:token')

export default ({ $axios, store, app }) => {
  const currentLocale = store.$i18n.localeProperties.code

  const translateLinks = (string, locale = currentLocale) =>
    translateContentLinks(string, locale, pathTranslations)

  $axios.onResponse(response => {
    if (response.status === 0) {
      throw createError(
        'Network Error',
        response.config,
        null,
        response.request
      )
    }
  })

  $axios.onError(error => {
    if (
      process.client &&
      error.config &&
      !error.config.__retriedAfterCookiePrune &&
      error.response?.status === 400 &&
      typeof error.response.data === 'string' &&
      error.response.data.includes('Request Header Or Cookie Too Large')
    ) {
      const freedBytes = pruneOversizedCookies({ purgeAll: true })
      if (freedBytes > 0 || cookieHeaderSize() <= MAX_COOKIE_HEADER_BYTES) {
        error.config.__retriedAfterCookiePrune = true
        return $axios.request(error.config)
      }
    }

    error.request_summary = {
      method: error.config?.method?.toUpperCase(),
      url: summarizeUrl(error.config?.url),
      params: Object.keys(error.config?.params || {}),
      status: error.response?.status,
      server: error.response?.headers?.server,
      cf_mitigated: error.response?.headers?.['cf-mitigated'],
    }

    delete error.config
    delete error.request
    if (error.response) {
      error.response = {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
      }
    }
  })

  $axios.onRequest(request => {
    withContextHeaders(request.headers, {
      locale: store.$i18n.localeProperties.code,
      country: priceCountry({
        accountCountry: store.$auth?.loggedIn
          ? store.$auth.user?.country?.iso
          : null,
        chosenCountry: store.state.chosen_country,
        locale: store.$i18n.localeProperties.code,
      }),
      visitorCountry: store.state.country,
      orderToken: app.$nuxtCookies.get('order_token'),
      favoriteToken: app.$nuxtCookies.get('favorite_token'),
    })
  })

  $axios.onResponse(response => {
    for (const property in response.headers) {
      if (property === 'x-vat-country') {
        store.commit('SET_VAT_COUNTRY', response.headers[property])
      }

      if (property === 'x-vat-rate') {
        store.commit('SET_VAT_RATE', parseFloat(response.headers[property]))
      }
    }

    if (currentLocale != 'fr') {
      for (const property in response.data.data) {
        if (
          propertiesToTranslate.includes(property) &&
          typeof response.data.data[property] === 'string' &&
          response.data.data[property] !== null
        ) {
          response.data.data[property] = translateLinks(
            response.data.data[property]
          )
        } else if (
          propertiesToTranslate.includes(property) &&
          typeof response.data.data[property] === 'object'
        ) {
          switch (property) {
            case 'variant':
              if (
                typeof response.data.data.variant === 'object' &&
                response.data.data.variant !== null
              ) {
                const variant = response.data.data.variant
                if (typeof variant.description_short === 'string') {
                  variant.description_short = translateLinks(
                    variant.description_short
                  )
                }
                if (Array.isArray(variant.descriptions)) {
                  variant.descriptions.forEach(descriptionBlock => {
                    if (typeof descriptionBlock.description === 'string') {
                      descriptionBlock.description = translateLinks(
                        descriptionBlock.description
                      )
                    }
                  })
                }
              }
              break
            case 'questions':
              if (
                typeof response.data.data.questions === 'object' &&
                response.data.data.questions !== null
              ) {
                for (const question of response.data.data.questions) {
                  question.answer = translateLinks(question.answer)
                }
              }
              break
          }
        }
      }
    }
  })
}
